<template>
  <UiDropdown ref="dropdown" :placement="'bottom-start'" :disabled="busy">
    <template #default>
      <slot :icon="reactionIcon">
        <button :disabled="disabled || busy" :class="['reactbtn', btnClass, reactionType, { 'animate-pulse': busy }]">
          <Transition
            mode="out-in"
            enter-active-class="ease-in-out duration-300 transform"
            enter-from-class="opacity-0 -translate-y-2"
            leave-active-class="ease-in-out duration-300 transform"
            leave-to-class="opacity-0 translate-y-2"
          >
            <UiIcon :icon="reactionIcon" class="w-full h-6" />
          </Transition>
          <div v-if="!minimized && exibirTexto" class="ml-2 grow text-left">{{ $t(reaction ? 'labels.reacted' : 'labels.react') }}</div>
        </button>
      </slot>
    </template>

    <template #content>
      <div :class="[typeButton == 'class' ? 'p-[0.65rem]' : 'p-2']">
        <div :class="['grid grid-cols-3 gap-2', {'grid-cols-5': typeButton == 'class'}]">
          <button
            v-for="(icon, key) in filteredIcons"
            :key="key"
            :class="[
              'p-2 border border-transparent rounded-lg hover:border-amber-500 transition-all',
              typeButton == 'class' ? 'p-[0.65rem]' : 'p-2'
            ]"
            @click="() => setReaction(key)"
          >
            <UiIcon
              :icon="icon"
              class="w-full h-6 text-center text-amber-500 dark:text-amber-400"
            />
          </button>
          <button
            v-if="reaction"
            :class="[
              'border border-red rounded-lg border-rose-400 bg-rose-100 hover:bg-rose-200 text-rose-500 transition-all',
              typeButton == 'class' ? 'p-[0.65rem]' : 'p-2'
            ]"
            @click="() => setReaction(null)"
          >
            <UiIcon icon="trash" class="w-full h-6 text-center" />
          </button>
        </div>
      </div>
    </template>
  </UiDropdown>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { toast } from '@/common/util'
import { useI18n } from 'vue-i18n'
import { useCursosStore, useFormacoesStore, useMidiaStore } from '@/stores'

import UiIcon from '@/components/ui/icon.vue'
import UiDropdown from '@/components/ui/dropdown.vue'
// import UiButton from '@/components/ui/button.vue'

const { t } = useI18n()

const emit = defineEmits(['onSuccess'])

const props = defineProps<{
  reaction?: string
  minimized?: boolean
  typeContent?: any
  contentId?: any
  qtd?: number
  disabled?: boolean
  typeButton?: string
  btnClass?: string
  exibirTexto?: boolean
}>()

const cursosStore = useCursosStore()
const formacoesStore = useFormacoesStore()
const midiaStore = useMidiaStore()

const busy = ref(false)

const icons = {
  heart: 'heart',
  like: 'thumb-up',
  dislike: 'thumb-down',
  confused: 'mood-puzzled',
  sad: 'mood-sad',
  surprised: 'mood-suprised',
  happy: 'mood-happy',
  smile: 'mood-smile',
  empty: 'mood-empty',
  angry: 'mood-angry',
  star: 'star'
}

const filteredIcons = computed(() => {
  const iconsToShow = { ...icons }
  // const toRemove = ['dislike', 'confused', 'sad', 'surprised', 'empty', 'angry']
  // toRemove.forEach(key => delete iconsToShow[key])
  return iconsToShow
})

const dropdown = ref<InstanceType<typeof UiDropdown>>()
const demoReaction = ref('heart')
//const excludedIcons = ['dislike', 'confused', 'sad', 'empty', 'angry']

// const randReaction = () => {
//   const keys = Object.keys(icons).filter(key => !excludedIcons.includes(key));
//   demoReaction.value = keys[Math.floor(Math.random() * keys.length)];
// }

const reactionIcon = computed(() => icons[props.reaction?.toLowerCase() || demoReaction.value?.toLowerCase()])

const setReaction = async (reaction?: string) => {
  let response = null

  const params = {
    id: props.contentId,
    reacao: reaction
  }

  try {
    busy.value = true
    if (!['curso', 'formacao', 'course', 'formation', 'trail', 'trilha'].includes(props.typeContent)) {
      response = await midiaStore.setReactionMedia(params)
    } else if (props.typeContent === 'curso' || props.typeContent === 'course') {
      response = await cursosStore.setReactionCourse(params)
    } else {
      response = await formacoesStore.setReactionFormation(params)
    }

    if (response.success) {
      toast.success({
        message: t(!reaction ? 'toasts.details.reactionCanceled' : 'toasts.details.reactionSuccess')
      })

      emit('onSuccess', { ...response.data, reacao: reaction })
      dropdown.value?.close()
    }
  } catch (error) {
    toast.error({
      message: t('feedback.somethingWrong')
    })
  } finally {
    busy.value = false
  }
}

const reactionType = computed(() => {
  if(['heart', 'like', 'surprised', 'happy', 'smile', 'star'].includes(props.reaction?.toLowerCase())) return 'positive'
  else if (['empty', 'confused'].includes(props.reaction?.toLowerCase())) return 'neutral'
  else if (['dislike', 'sad', 'angry'].includes(props.reaction?.toLowerCase())) return 'negative'
  else return ''
})

//randReaction()
</script>

<style scoped lang="scss">
button:disabled {
  @apply cursor-not-allowed text-[#a3a3a3] border-[#a3a3a3] dark:text-[#a3a3a3] dark:border-[#a3a3a3];
}

.reactbtn {
  @apply flex border rounded-md items-center justify-center overflow-hidden bg-transparent border-[--c-primary-600] text-[--c-primary-600] dark:border-[#FDFDFD] dark:text-[#FDFDFD];
  .r-icon {
    @apply bg-transparent;
  }

  &.positive {
    @apply border-emerald-700 text-emerald-700 dark:border-emerald-300 dark:text-emerald-300;
    .r-icon {
      @apply bg-emerald-200 dark:bg-emerald-700;
    }
  }

  &.neutral {
    @apply  border-amber-700 text-amber-700 dark:border-amber-300 dark:text-amber-300 dark:bg-amber-900/20;
    .r-icon {
      @apply bg-amber-200 dark:bg-amber-700;
    }
  }

  &.negative {
    @apply  border-rose-700 text-rose-700 dark:border-rose-300 dark:text-rose-300 dark:bg-rose-900/20;
    .r-icon {
      @apply bg-rose-200 dark:bg-rose-700;
    }
  }
}
</style>
